(() => {
  "use strict";

  if (window.TNPrestigePoster) return;

  const MAX_BYTES = 25 * 1024 * 1024;
  const MAX_CUSTOM_INCHES = 118.11;
  const LARGEST_STANDARD_AREA = 36 * 36;
  const states = new Map();
  const sizeStates = new Map();
  const customSizeTimers = new WeakMap();
  let repaintQueued = false;
  const STYLE_ORDERS = {
    UNFRAMED: [0, 5, 1, 2],
    FRAMED: [4, 2, 3, 0],
  };
  const FLOATER_INDEXES = {
    WHITE: 6,
    WALNUT: 7,
    GOLD: 8,
    NATURAL: 9,
    BLACK: 10,
    OAK: 11,
  };
  const STANDARD_PRICES = [
    [8, 8, 30, 40, 70],
    [8, 12, 35, 45, 80],
    [12, 8, 35, 35, 80],
    [12, 12, 45, 55, 105],
    [12, 16, 50, 60, 125],
    [12, 18, 55, 65, 140],
    [16, 12, 50, 60, 125],
    [16, 16, 75, 85, 175],
    [16, 20, 80, 90, 205],
    [16, 24, 100, 110, 245],
    [18, 12, 55, 65, 140],
    [20, 16, 80, 90, 205],
    [20, 20, 100, 110, 265],
    [20, 30, 125, 140, 310],
    [24, 16, 100, 110, 245],
    [24, 24, 125, 140, 335],
    [24, 36, 160, 180, 435],
    [30, 20, 125, 140, 310],
    [30, 30, 150, 190, 390],
    [36, 24, 160, 180, 435],
    [36, 36, 180, 200, 505],
  ];

  function selectorValue(value) {
    if (window.CSS?.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, "\\$&");
  }

  function uploaderFor(formId) {
    return document.querySelector(`[data-tn-prestige-uploader][data-form-id="${selectorValue(formId)}"]`);
  }

  function sectionFor(uploader) {
    return uploader?.closest(".shopify-section--main-product")
      || uploader?.closest('[id^="shopify-section-"]')
      || document;
  }

  function updateStatus(uploader, text, tone = "") {
    const status = uploader?.querySelector("[data-tn-prestige-status]");
    if (!status) return;
    status.textContent = text;
    status.dataset.tone = tone;
    status.hidden = !text;
  }

  function updateProgress(uploader, percent, visible = true) {
    const shell = uploader?.querySelector("[data-tn-progress-shell]");
    const progress = uploader?.querySelector("[data-tn-prestige-progress]");
    if (!shell || !progress) return;
    shell.hidden = !visible;
    progress.style.width = `${Math.max(0, Math.min(100, percent))}%`;
  }

  function updateProperties(uploader, state) {
    if (!uploader || !state) return;
    const upload = uploader.querySelector("[data-tn-upload-property]");
    const orientation = uploader.querySelector("[data-tn-orientation-property]");
    const fileName = uploader.querySelector("[data-tn-file-property]");
    if (upload) upload.value = state.uploadId || "";
    if (orientation) orientation.value = state.orientation || "";
    if (fileName) fileName.value = state.fileName || "";
  }

  function restoreFileInput(uploader, state) {
    const input = uploader?.querySelector("[data-tn-prestige-file]");
    if (!input || input.files?.length || !state?.file || typeof DataTransfer === "undefined") return;
    const transfer = new DataTransfer();
    transfer.items.add(state.file);
    input.files = transfer.files;
  }

  function setPurchaseEnabled(formId, enabled) {
    const form = document.getElementById(formId);
    const candidates = [
      ...(form ? form.querySelectorAll('button[type="submit"], input[type="submit"]') : []),
      ...document.querySelectorAll(`button[type="submit"][form="${selectorValue(formId)}"], input[type="submit"][form="${selectorValue(formId)}"]`),
    ];
    candidates.forEach((control) => {
      control.disabled = !enabled;
      control.setAttribute("aria-disabled", enabled ? "false" : "true");
      if (!enabled) control.dataset.tnUploadLocked = "true";
      else delete control.dataset.tnUploadLocked;
    });
  }

  function uploadCanPurchase(state) {
    return Boolean(
      state?.ready
      && (state.allowOfflinePurchase || state.backupStatus === "saved"),
    );
  }

  function activate(section, index) {
    section.querySelectorAll("[data-tn-gallery]").forEach((gallery) => {
      gallery.querySelectorAll("[data-tn-mockup-index]").forEach((cell) => {
        cell.classList.toggle("is-active", Number(cell.dataset.tnMockupIndex) === index);
      });
    });
  }

  function selectedVariant(section) {
    const data = section.querySelector("variant-picker script[data-variant]");
    if (!data?.textContent) return null;
    try {
      return JSON.parse(data.textContent);
    } catch {
      return null;
    }
  }

  function selectedOption(section, name) {
    const escapedName = selectorValue(`options[${name}]`);
    const checked = section.querySelector(`input[name="${escapedName}"]:checked`);
    if (checked) return checked.value;
    const select = section.querySelector(`select[name="${escapedName}"]`);
    return select?.value || "";
  }

  function orientationFor(width, height) {
    const ratio = width / height;
    if (ratio > 1.08) return "landscape";
    if (ratio < .92) return "portrait";
    return "square";
  }

  function parseStandardSize(label) {
    const match = String(label).match(/^\s*(\d+(?:\.\d+)?)\s*X\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;
    return {
      width: Number(match[1]),
      height: Number(match[2]),
    };
  }

  function sizeStateFor(formId) {
    if (!sizeStates.has(formId)) {
      sizeStates.set(formId, {
        custom: false,
        width: "",
        height: "",
        price: null,
        adjusting: false,
      });
    }
    return sizeStates.get(formId);
  }

  function styleFor(section) {
    const variant = selectedVariant(section);
    const checkedStyle = section.querySelector('input[data-option-position="2"]:checked');
    const checkedLabel = checkedStyle?.dataset.tnOptionLabel || checkedStyle?.value;
    return String(checkedLabel || variant?.option2 || selectedOption(section, "STYLE") || "UNFRAMED")
      .trim()
      .toUpperCase();
  }

  function nearestStandard(width, height) {
    const area = width * height;
    return [...STANDARD_PRICES].sort((a, b) => {
      const areaA = a[0] * a[1];
      const areaB = b[0] * b[1];
      return Math.abs(areaA - area) - Math.abs(areaB - area)
        || areaA - areaB
        || a[0] - b[0]
        || a[1] - b[1];
    })[0];
  }

  function customPrice(width, height, style) {
    const area = width * height;
    const nearest = nearestStandard(width, height);
    if (area > LARGEST_STANDARD_AREA) {
      if (style.includes("FLOATER")) {
        return Math.max(505, Math.round((505 * area / LARGEST_STANDARD_AREA) / 50) * 50);
      }
      if (style === "FRAMED") {
        return Math.max(200, Math.round((200 * area / LARGEST_STANDARD_AREA) / 25) * 25);
      }
      return Math.max(180, Math.round((180 * area / LARGEST_STANDARD_AREA) / 25) * 25);
    }
    if (style.includes("FLOATER")) return nearest[4];
    if (style === "FRAMED") return nearest[3];
    return nearest[2];
  }

  function chooseRadio(input) {
    if (!input || input.checked) return false;
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  function firstStandardSize(section, orientation) {
    return [...section.querySelectorAll("[data-tn-standard-size]")].find((label) => {
      if (label.hidden) return false;
      const size = parseStandardSize(label.dataset.tnStandardSize);
      return size && orientationFor(size.width, size.height) === orientation;
    })?.querySelector('input[type="radio"]');
  }

  function applyOrientationSizes(section, state = null) {
    const orientation = state?.orientation;
    if (!orientation) return;
    const formId = section.querySelector("[data-tn-prestige-uploader]")?.dataset.formId;
    const sizeState = formId ? sizeStateFor(formId) : null;

    section.querySelectorAll("[data-tn-standard-size]").forEach((label) => {
      const size = parseStandardSize(label.dataset.tnStandardSize);
      label.hidden = !size || orientationFor(size.width, size.height) !== orientation;
    });

    if (sizeState?.custom || sizeState?.adjusting) return;
    const variant = selectedVariant(section);
    const selectedSize = parseStandardSize(variant?.option1 || "");
    if (selectedSize && orientationFor(selectedSize.width, selectedSize.height) === orientation) return;

    const first = firstStandardSize(section, orientation);
    if (!first) return;
    sizeState.adjusting = true;
    chooseRadio(first);
    window.setTimeout(() => {
      sizeState.adjusting = false;
    }, 250);
  }

  function setCustomMessage(panel, text) {
    const message = panel?.querySelector("[data-tn-custom-message]");
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
  }

  function syncCustomProperties(panel, state) {
    const widthProperty = panel.querySelector("[data-tn-custom-width-property]");
    const heightProperty = panel.querySelector("[data-tn-custom-height-property]");
    const priceProperty = panel.querySelector("[data-tn-custom-price-property]");
    if (widthProperty) widthProperty.value = state.custom ? state.width : "";
    if (heightProperty) heightProperty.value = state.custom ? state.height : "";
    if (priceProperty) priceProperty.value = state.custom && state.price ? `AED ${state.price}` : "";
  }

  function calculateCustomSize(panel, options = {}) {
    if (!panel) return false;
    const formId = panel.dataset.formId;
    const state = sizeStateFor(formId);
    const section = sectionFor(uploaderFor(formId));
    const uploadState = states.get(formId);
    const widthInput = panel.querySelector("[data-tn-custom-width]");
    const heightInput = panel.querySelector("[data-tn-custom-height]");
    const priceOutput = panel.querySelector("[data-tn-custom-price]");
    const width = Number(widthInput?.value);
    const height = Number(heightInput?.value);

    state.width = widthInput?.value || "";
    state.height = heightInput?.value || "";
    state.price = null;
    if (priceOutput) priceOutput.hidden = true;
    syncCustomProperties(panel, state);

    if (!state.custom) return true;
    if (!uploadState?.orientation) {
      setCustomMessage(panel, "Upload your artwork first.");
      setPurchaseEnabled(formId, false);
      return false;
    }
    if (!Number.isFinite(width) || !Number.isFinite(height) || width < 8 || height < 8 || width > MAX_CUSTOM_INCHES || height > MAX_CUSTOM_INCHES) {
      setCustomMessage(panel, "Enter width and height from 8 inches up to 3 metres.");
      setPurchaseEnabled(formId, false);
      return false;
    }
    if (orientationFor(width, height) !== uploadState.orientation) {
      setCustomMessage(panel, `Use ${uploadState.orientation} dimensions for this artwork.`);
      setPurchaseEnabled(formId, false);
      return false;
    }

    const price = customPrice(width, height, options.style || styleFor(section));
    const tierName = `CUSTOM AED ${price}`;
    const tier = section.querySelector(`[data-tn-custom-tier="${selectorValue(tierName)}"]`);
    if (!tier) {
      setCustomMessage(panel, "This custom price is not available yet. Refresh after importing the new variants.");
      setPurchaseEnabled(formId, false);
      return false;
    }

    state.price = price;
    setCustomMessage(panel, "");
    syncCustomProperties(panel, state);
    if (priceOutput) {
      priceOutput.textContent = `${width} × ${height} inch · AED ${price}`;
      priceOutput.hidden = false;
    }
    if (!options.skipVariantChange) chooseRadio(tier);
    setPurchaseEnabled(formId, uploadCanPurchase(uploadState));
    return true;
  }

  async function selectCustomStyle(styleInput) {
    const formId = styleInput?.getAttribute("form");
    const uploader = uploaderFor(formId);
    const section = sectionFor(uploader);
    const panel = section.querySelector("[data-tn-custom-size]");
    const sizeState = sizeStateFor(formId);
    const picker = section.querySelector(`variant-picker[form-id="${selectorValue(formId)}"]`);
    if (!uploader || !panel || !sizeState.custom || !picker?.selectCombination) return false;

    const style = String(styleInput.dataset.tnOptionLabel || styleInput.value || "UNFRAMED")
      .trim()
      .toUpperCase();
    let color = "";
    let colorInput = null;
    if (style.includes("FLOATER")) {
      const selectedColor = section.querySelector('input[data-option-position="3"]:checked:not([value="None"])');
      const whiteColor = [...section.querySelectorAll('input[data-option-position="3"]')].find((input) => (
        String(input.dataset.tnOptionLabel || input.value).trim().toUpperCase() === "WHITE"
      ));
      const firstColor = section.querySelector('input[data-option-position="3"]:not([value="None"])');
      colorInput = selectedColor || whiteColor || firstColor;
      color = String(colorInput?.dataset.tnOptionLabel || colorInput?.value || "WHITE")
        .trim()
        .toUpperCase();
    }

    applyGalleryRule(section, states.get(formId), { style, color });
    if (!calculateCustomSize(panel, { skipVariantChange: true, style })) return true;

    const tierName = `CUSTOM AED ${sizeState.price}`;
    const tier = section.querySelector(`[data-tn-custom-tier="${selectorValue(tierName)}"]`);
    if (!tier) return true;

    const optionValues = [tier.value, styleInput.value];
    if (colorInput) optionValues.push(colorInput.value);

    sizeState.adjusting = true;
    setPurchaseEnabled(formId, false);
    try {
      await picker.selectCombination({
        optionValues,
        productChange: false,
      });
    } finally {
      sizeState.adjusting = false;
      scheduleGalleryRule(section);
    }
    return true;
  }

  function restoreCustomSize(section) {
    const panel = section.querySelector("[data-tn-custom-size]");
    if (!panel) return;
    const formId = panel.dataset.formId;
    const sizeState = sizeStateFor(formId);
    const variant = selectedVariant(section);
    if (String(variant?.option1 || "").startsWith("CUSTOM AED")) sizeState.custom = true;
    panel.classList.toggle("is-active", sizeState.custom);
    const fields = panel.querySelector("[data-tn-custom-size-fields]");
    if (fields) fields.hidden = !sizeState.custom;
    const width = panel.querySelector("[data-tn-custom-width]");
    const height = panel.querySelector("[data-tn-custom-height]");
    if (width) width.value = sizeState.width;
    if (height) height.value = sizeState.height;
    calculateCustomSize(panel);
  }

  function galleryIndexes(section, options = {}) {
    const variant = selectedVariant(section);
    const checkedStyle = section.querySelector('input[data-option-position="2"]:checked');
    const style = String(
      options.style
      || checkedStyle?.dataset.tnOptionLabel
      || checkedStyle?.value
      || selectedOption(section, "STYLE")
      || variant?.option2
      || "UNFRAMED",
    ).trim().toUpperCase();

    if (style.includes("FLOATER")) {
      const checkedColor = section.querySelector('input[data-option-position="3"]:checked:not([value="None"])');
      const color = String(
        options.color
        || checkedColor?.dataset.tnOptionLabel
        || checkedColor?.value
        || selectedOption(section, "FLOATER COLOR")
        || variant?.option3
        || "WHITE",
      ).trim().toUpperCase();
      return [FLOATER_INDEXES[color] ?? FLOATER_INDEXES.WHITE];
    }

    return STYLE_ORDERS[style] || STYLE_ORDERS.UNFRAMED;
  }

  function applyGalleryRule(section, state = null, options = {}) {
    const visibleIndexes = galleryIndexes(section, options);
    const visibleSet = new Set(visibleIndexes);

    section.querySelectorAll("[data-tn-gallery]").forEach((gallery) => {
      gallery.querySelectorAll("[data-tn-mockup-index]").forEach((cell) => {
        const index = Number(cell.dataset.tnMockupIndex);
        const visible = visibleSet.has(index);
        cell.classList.toggle("tn-variant-hidden", !visible);
        cell.style.order = visible ? String(visibleIndexes.indexOf(index)) : "";
      });
    });

    const activeIndex = state?.activeIndex;
    const nextIndex = visibleSet.has(activeIndex) ? activeIndex : visibleIndexes[0];
    if (state) state.activeIndex = nextIndex;
    activate(section, nextIndex);
  }

  function scheduleGalleryRule(target = document) {
    const section = target.closest?.(".shopify-section--main-product")
      || target.closest?.('[id^="shopify-section-"]')
      || target.querySelector?.(".shopify-section--main-product")
      || document;
    [0, 40, 140].forEach((delay) => {
      window.setTimeout(() => {
        const uploader = section.querySelector?.("[data-tn-prestige-uploader]");
        const state = uploader ? states.get(uploader.dataset.formId) : null;
        if (state?.ready) {
          paint(state);
        } else {
          applyGalleryRule(section, state);
          applyOrientationSizes(section, state);
          restoreCustomSize(section);
        }
      }, delay);
    });
  }

  function paint(state) {
    const uploader = uploaderFor(state.formId);
    if (!uploader) return;
    const section = sectionFor(uploader);
    section.querySelectorAll("[data-tn-gallery]").forEach((gallery) => {
      state.items.forEach((item, index) => {
        const cell = gallery.querySelector(`[data-tn-mockup-index="${index}"]`);
        if (!cell) return;
        const image = cell.querySelector("[data-tn-generated-image]");
        const placeholder = cell.querySelector(".tn-mockup-placeholder");
        if (image) {
          image.src = item.url;
          image.alt = item.label;
          image.hidden = false;
        }
        if (placeholder) placeholder.hidden = true;
        cell.dataset.tnReady = "true";
      });
    });
    applyGalleryRule(section, state);
    applyOrientationSizes(section, state);
    restoreCustomSize(section);
    updateProperties(uploader, state);
    restoreFileInput(uploader, state);
    if (state.ready) {
      updateStatus(uploader, "");
      updateProgress(uploader, 100, true);
      const sizeState = sizeStateFor(state.formId);
      setPurchaseEnabled(
        state.formId,
        uploadCanPurchase(state) && (!sizeState.custom || Boolean(sizeState.price)),
      );
      window.setTimeout(() => {
        if (states.get(state.formId) === state) updateProgress(uploader, 100, false);
      }, 650);
    }
  }

  async function uploadOriginal(uploader, file, uploadId) {
    const endpoint = String(uploader.dataset.uploadEndpoint || "").replace(/\/+$/, "");
    if (!endpoint) throw new Error("The private laptop inbox URL has not been configured.");
    const timeoutSeconds = Math.max(5, Math.min(30, Number(uploader.dataset.uploadTimeout) || 12));
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutSeconds * 1000);
    try {
      const response = await fetch(`${endpoint}/api/uploads`, {
        method: "POST",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "X-Upload-Id": uploadId,
          "X-File-Name": encodeURIComponent(file.name),
          "X-Page-Url": encodeURIComponent(location.href),
        },
        body: file,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`The private upload inbox returned ${response.status}.`);
      return response.json();
    } catch (error) {
      if (error?.name === "AbortError") {
        throw new Error(`The private laptop inbox did not answer within ${timeoutSeconds} seconds.`);
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function resetGallery(section) {
    section.querySelectorAll("[data-tn-generated-image]").forEach((image) => {
      image.removeAttribute("src");
      image.hidden = true;
    });
    section.querySelectorAll(".tn-mockup-placeholder").forEach((placeholder) => {
      placeholder.hidden = false;
    });
    section.querySelectorAll("[data-tn-mockup-index]").forEach((cell) => {
      delete cell.dataset.tnReady;
    });
    applyGalleryRule(section);
  }

  function decodeMockup(item) {
    return new Promise((resolve) => {
      const image = new Image();
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const finishAfterDecode = () => {
        if (typeof image.decode !== "function") {
          finish();
          return;
        }
        image.decode().catch(() => {}).then(finish);
      };
      image.decoding = "async";
      image.onload = finishAfterDecode;
      image.onerror = finish;
      image.src = item.url;
      if (image.complete) {
        if (image.naturalWidth) finishAfterDecode();
        else finish();
      }
    });
  }

  async function preloadMockups(items) {
    const decodeAll = Promise.allSettled(items.map(decodeMockup));
    const safetyTimeout = new Promise((resolve) => {
      window.setTimeout(resolve, 4000);
    });
    await Promise.race([decodeAll, safetyTimeout]);
  }

  async function handleFile(input) {
    const uploader = input.closest("[data-tn-prestige-uploader]");
    const file = input.files?.[0];
    if (!uploader || !file) return;
    const formId = uploader.dataset.formId;
    if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      updateStatus(uploader, "Please choose a JPG, PNG, or WebP image.", "bad");
      input.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      updateStatus(uploader, "That image is larger than 25 MB. Please choose a smaller copy.", "bad");
      input.value = "";
      return;
    }
    if (!window.TNPosterRenderer) {
      updateStatus(uploader, "The mockup renderer did not load. Please refresh and try again.", "bad");
      return;
    }

    const previous = states.get(formId);
    previous?.items?.forEach((item) => URL.revokeObjectURL(item.url));
    const state = {
      formId,
      uploadId: window.TNPosterRenderer.randomId(),
      file,
      fileName: file.name,
      orientation: "",
      items: [],
      activeIndex: 0,
      ready: false,
      backupStatus: "pending",
      allowOfflinePurchase: uploader.dataset.allowOfflinePurchase !== "false",
    };
    states.set(formId, state);
    setPurchaseEnabled(formId, false);
    resetGallery(sectionFor(uploader));
    updateProperties(uploader, state);

    const selection = uploader.querySelector("[data-tn-prestige-selection]");
    const thumb = uploader.querySelector("[data-tn-prestige-thumb]");
    const name = uploader.querySelector("[data-tn-prestige-name]");
    const details = uploader.querySelector("[data-tn-prestige-details]");
    if (selection) selection.hidden = false;
    if (thumb) thumb.src = URL.createObjectURL(file);
    if (name) name.textContent = file.name;
    if (details) details.textContent = `${(file.size / 1048576).toFixed(1)} MB`;
    updateProgress(uploader, 4, true);
    updateStatus(uploader, "");

    try {
      const uploadPromise = uploadOriginal(uploader, file, state.uploadId)
        .then((result) => ({ ok: Boolean(result?.ok), result }))
        .catch((error) => ({ ok: false, error }));
      const renderPromise = window.TNPosterRenderer.createMockups(file, uploader, ({ completed, total, label }) => {
        if (states.get(formId) !== state) return;
        updateProgress(uploader, Math.max(6, Math.round((completed / total) * 92)), true);
        updateStatus(uploader, "");
      });
      const renderResult = await renderPromise;
      if (states.get(formId) !== state) {
        renderResult.items.forEach((item) => URL.revokeObjectURL(item.url));
        return;
      }
      updateProgress(uploader, 96, true);
      await preloadMockups(renderResult.items);
      if (states.get(formId) !== state) {
        renderResult.items.forEach((item) => URL.revokeObjectURL(item.url));
        return;
      }
      state.items = renderResult.items;
      state.orientation = renderResult.orientation;
      state.width = renderResult.width;
      state.height = renderResult.height;
      state.ready = true;
      if (details) details.textContent = `${(file.size / 1048576).toFixed(1)} MB · ${state.width} × ${state.height} · ${state.orientation}`;
      paint(state);

      uploadPromise.then((uploadResult) => {
        if (states.get(formId) !== state) return;
        state.backupStatus = uploadResult.ok ? "saved" : "failed";
        const sizeState = sizeStateFor(formId);
        setPurchaseEnabled(
          formId,
          uploadCanPurchase(state) && (!sizeState.custom || Boolean(sizeState.price)),
        );
        if (uploadResult.ok) {
          updateStatus(uploader, "");
          return;
        }
        console.warn("[TN artwork backup]", uploadResult.error || "The private backup was unavailable.");
        if (state.allowOfflinePurchase) {
          updateStatus(uploader, "");
        } else {
          updateStatus(
            uploader,
            "We could not securely save your artwork. Please upload it again.",
            "bad",
          );
        }
      });
    } catch (error) {
      if (states.get(formId) !== state) return;
      state.ready = false;
      updateProgress(uploader, 0, false);
      setPurchaseEnabled(formId, false);
      updateStatus(uploader, `${error.message || "The upload failed"} The cart remains locked so your artwork cannot be lost.`, "bad");
    }
  }

  document.addEventListener("change", (event) => {
    const customStyle = event.target.closest('input[data-option-position="2"]');
    if (customStyle) {
      const formId = customStyle.getAttribute("form");
      const sizeState = formId ? sizeStateFor(formId) : null;
      if (sizeState?.custom) {
        event.stopImmediatePropagation();
        selectCustomStyle(customStyle).catch(() => {
          setPurchaseEnabled(formId, false);
          const panel = sectionFor(uploaderFor(formId)).querySelector("[data-tn-custom-size]");
          setCustomMessage(panel, "Please select that style again.");
        });
        return;
      }
    }

    const input = event.target.closest("[data-tn-prestige-file]");
    if (input) handleFile(input);
    const standardSize = event.target.closest("[data-tn-standard-size]");
    if (standardSize) {
      const section = standardSize.closest(".shopify-section--main-product")
        || standardSize.closest('[id^="shopify-section-"]')
        || document;
      const uploader = section.querySelector("[data-tn-prestige-uploader]");
      const panel = section.querySelector("[data-tn-custom-size]");
      if (uploader && panel) {
        const sizeState = sizeStateFor(uploader.dataset.formId);
        sizeState.custom = false;
        sizeState.price = null;
        panel.classList.remove("is-active");
        const fields = panel.querySelector("[data-tn-custom-size-fields]");
        if (fields) fields.hidden = true;
        setCustomMessage(panel, "");
        syncCustomProperties(panel, sizeState);
        setPurchaseEnabled(uploader.dataset.formId, uploadCanPurchase(states.get(uploader.dataset.formId)));
      }
    }
    if (event.target.closest("variant-picker, product-form, form")) {
      scheduleGalleryRule(event.target);
    }
  }, true);

  document.addEventListener("input", (event) => {
    const panel = event.target.closest("[data-tn-custom-size]");
    if (!panel || !event.target.matches("[data-tn-custom-width], [data-tn-custom-height]")) return;
    const previousTimer = customSizeTimers.get(panel);
    if (previousTimer) window.clearTimeout(previousTimer);
    const nextTimer = window.setTimeout(() => {
      customSizeTimers.delete(panel);
      calculateCustomSize(panel);
    }, 280);
    customSizeTimers.set(panel, nextTimer);
  });

  document.addEventListener("variant:change", (event) => {
    scheduleGalleryRule(event.target);
  }, true);

  document.addEventListener("product:rerender", (event) => {
    scheduleGalleryRule(event.target);
  }, true);

  document.addEventListener("dragenter", (event) => {
    const drop = event.target.closest("[data-tn-prestige-drop]");
    if (!drop) return;
    event.preventDefault();
    drop.classList.add("is-dragging");
  });

  document.addEventListener("dragover", (event) => {
    const drop = event.target.closest("[data-tn-prestige-drop]");
    if (drop) event.preventDefault();
  });

  document.addEventListener("dragleave", (event) => {
    event.target.closest("[data-tn-prestige-drop]")?.classList.remove("is-dragging");
  });

  document.addEventListener("drop", (event) => {
    const drop = event.target.closest("[data-tn-prestige-drop]");
    if (!drop) return;
    event.preventDefault();
    drop.classList.remove("is-dragging");
    const input = drop.querySelector("[data-tn-prestige-file]");
    if (!input || !event.dataTransfer?.files?.length) return;
    const transfer = new DataTransfer();
    transfer.items.add(event.dataTransfer.files[0]);
    input.files = transfer.files;
    handleFile(input);
  });

  document.addEventListener("click", (event) => {
    const customToggle = event.target.closest("[data-tn-custom-size-toggle]");
    if (customToggle) {
      const panel = customToggle.closest("[data-tn-custom-size]");
      const formId = panel.dataset.formId;
      const sizeState = sizeStateFor(formId);
      const uploadState = states.get(formId);
      const section = sectionFor(uploaderFor(formId));
      sizeState.custom = !sizeState.custom;
      sizeState.price = null;
      panel.classList.toggle("is-active", sizeState.custom);
      const fields = panel.querySelector("[data-tn-custom-size-fields]");
      if (fields) fields.hidden = !sizeState.custom;

      if (sizeState.custom) {
        calculateCustomSize(panel);
        panel.querySelector("[data-tn-custom-width]")?.focus();
      } else {
        setCustomMessage(panel, "");
        const priceOutput = panel.querySelector("[data-tn-custom-price]");
        if (priceOutput) priceOutput.hidden = true;
        syncCustomProperties(panel, sizeState);
        const first = uploadState?.orientation
          ? firstStandardSize(section, uploadState.orientation)
          : section.querySelector("[data-tn-standard-size] input");
        chooseRadio(first);
        setPurchaseEnabled(formId, uploadCanPurchase(uploadState));
      }
      return;
    }

    const cell = event.target.closest(".thumb_gallery [data-tn-mockup-index]");
    if (!cell) return;
    const section = cell.closest(".shopify-section--main-product") || cell.closest('[id^="shopify-section-"]') || document;
    const index = Number(cell.dataset.tnMockupIndex);
    if (cell.classList.contains("tn-variant-hidden")) return;
    const uploader = section.querySelector("[data-tn-prestige-uploader]");
    const state = uploader ? states.get(uploader.dataset.formId) : null;
    if (!state?.items[index]) return;
    state.activeIndex = index;
    activate(section, index);
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.id) return;
    const uploader = uploaderFor(form.id);
    if (!uploader) return;
    const state = states.get(form.id);
    const customPanel = sectionFor(uploader).querySelector("[data-tn-custom-size]");
    const sizeState = sizeStateFor(form.id);
    if (sizeState.custom && !calculateCustomSize(customPanel)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      customPanel?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (uploadCanPurchase(state)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    updateStatus(
      uploader,
      state?.ready
        ? "We could not securely save your artwork. Please upload it again."
        : "Please upload your image and wait for all 12 previews before adding this poster to cart.",
      "bad",
    );
    uploader.scrollIntoView({ behavior: "smooth", block: "center" });
  }, true);

  function rehydrate() {
    repaintQueued = false;
    document.querySelectorAll("[data-tn-prestige-uploader]").forEach((uploader) => {
      const formId = uploader.dataset.formId;
      const state = states.get(formId);
      if (state) paint(state);
      else {
        setPurchaseEnabled(formId, false);
        const section = sectionFor(uploader);
        applyGalleryRule(section);
        restoreCustomSize(section);
      }
    });
  }

  const observer = new MutationObserver((records) => {
    const relevant = records.some((record) => [...record.addedNodes].some((node) => (
      node.nodeType === 1
      && (
        node.matches?.("[data-tn-prestige-uploader], [data-tn-gallery], product-rerender")
        || node.querySelector?.("[data-tn-prestige-uploader], [data-tn-gallery]")
      )
    )));
    if (!relevant) return;
    if (repaintQueued) return;
    repaintQueued = true;
    requestAnimationFrame(rehydrate);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.TNPrestigePoster = {
    states,
    rehydrate,
    applyGalleryRule,
    orientationFor,
    priceForCustomSize: customPrice,
  };
  rehydrate();
})();
